/**
 * Attaching a job-note photo to a proposal or invoice, moved unchanged from
 * `ProposalInvoicePhotoSelector`'s Add selected (see `documentPhotoRequests`).
 */
import {
  type CompressionOptions,
  EventPhoto,
  File as KalosFile,
  URLObject,
} from '@kalos/kalos-rpc';

import {
  convertHeicToJpeg,
  EventClientService,
  FileClientService,
  S3ClientService,
} from '../../../tools/helpers';
import {
  documentPhotoFilenamePrefix,
  type DocumentPhotoTarget,
  type MemoImageListItem,
} from './documentPhotoRequests';

/**
 * Copy one job-note photo into the document's bucket and attach it, tagged with the system its
 * diagnostic note documents (`serviceItemId`, keyed on the ORIGINAL source fileId). Throws on
 * failure.
 */
export const copyNotePhotoToDocument = async (
  photo: MemoImageListItem,
  target: DocumentPhotoTarget & { serviceItemId: number },
): Promise<EventPhoto> => {
  const { eventId, filter, bucket, serviceItemId } = target;
  // Check if this is a HEIC file
  const isHeic =
    photo.fileMimeType === 'image/heic' ||
    photo.fileMimeType === 'image/heif' ||
    photo.fileName.toLowerCase().endsWith('.heic');

  let sourceFileName = photo.fileName;
  let sourceBucket = photo.fileBucket;
  let finalMimeType = photo.fileMimeType;

  // If HEIC, download, convert, and upload to temporary location
  if (isHeic) {
    try {
      // Download the HEIC file
      const url = URLObject.create({
        bucket: photo.fileBucket,
        key: photo.fileName,
      });

      const urlRes = await S3ClientService.GetDownloadURL(url);
      if (!urlRes?.url) {
        throw new Error('Failed to get download URL for HEIC file');
      }

      const downloadRes = await fetch(urlRes.url);
      if (downloadRes.status !== 200) {
        throw new Error('Failed to download HEIC file');
      }

      const arrayBuffer = await downloadRes.arrayBuffer();
      const fileData = new Uint8Array(arrayBuffer);

      // Convert HEIC to JPEG
      const convertedBlob = await convertHeicToJpeg(fileData, photo.fileName, photo.fileMimeType);
      const convertedBuffer = await convertedBlob.arrayBuffer();
      const convertedData = new Uint8Array(convertedBuffer);

      // Generate a filename for the converted JPEG with .jpg extension
      const baseName = photo.fileName.replace(/\.[^/.]+$/, '');
      const convertedFileName = `converted-heic-${Date.now()}-${baseName}.jpg`;

      // Convert Uint8Array to base64 string for upload
      let binaryString = '';
      for (let i = 0; i < convertedData.length; i++) {
        binaryString += String.fromCharCode(convertedData[i]);
      }
      const base64String = btoa(binaryString);
      const dataUrl = `data:image/jpeg;base64,${base64String}`;

      // Upload converted file to memo bucket for processing
      const uploadResult = await S3ClientService.uploadFileToS3Bucket(
        convertedFileName,
        dataUrl,
        'testbuckethelios',
      );

      if (uploadResult !== 'ok') {
        throw new Error('Failed to upload converted HEIC file');
      }

      // Update source to point to the converted file
      sourceFileName = convertedFileName;
      sourceBucket = 'testbuckethelios';
      finalMimeType = 'image/jpeg';
    } catch (conversionError) {
      console.error('Error converting HEIC file:', conversionError);
      // Fall back to original file if conversion fails
      sourceFileName = photo.fileName;
      sourceBucket = photo.fileBucket;
      finalMimeType = photo.fileMimeType;
    }
  }

  // Generate a new unique filename
  const baseName = sourceFileName.replace(/\.[^/.]+$/, '');
  const extension = finalMimeType === 'image/jpeg' ? 'jpg' : sourceFileName.split('.').pop();
  const newFileName = `${documentPhotoFilenamePrefix(target)}-${Date.now()}-${baseName}.${extension}`;

  // Define source and destination
  const source = {
    bucket: sourceBucket,
    key: sourceFileName,
  };

  const destination = {
    bucket: bucket,
    key: newFileName,
  };

  // Check if this is a WebP file to skip compression
  const isWebP = finalMimeType === 'image/webp' || sourceFileName.toLowerCase().endsWith('.webp');

  let copyResult: string;
  let finalFileMimeType: string;

  if (isWebP) {
    // For WebP files, just move without compression
    copyResult = await S3ClientService.moveFileBetweenS3Buckets(
      source,
      destination,
      true, // preserve the source file
    );
    finalFileMimeType = 'image/webp';
  } else {
    // For non-WebP files, use compression
    const compressionOptions: CompressionOptions = {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 80,
      format: 'JPEG',
    };

    copyResult = await S3ClientService.compressAndMoveFile(
      source,
      destination,
      compressionOptions,
      true, // preserve the source file
    );
    finalFileMimeType = 'image/jpeg'; // Since we're converting to JPEG in compression
  }

  if (copyResult === 'nok') {
    throw new Error(`Failed to ${isWebP ? 'move' : 'compress and copy'} file: ${sourceFileName}`);
  }

  // Create a new file record for the copied file
  const fileRes = await FileClientService.Create(
    KalosFile.create({
      name: newFileName,
      bucket: bucket,
      mimeType: finalFileMimeType,
    }),
  );

  if (!fileRes || !fileRes.id) {
    throw new Error(`Failed to create file record for: ${newFileName}`);
  }

  // Create EventPhoto with the appropriate filter fields and include flags
  // Proposal context: set includeInProposal=true, includeInInvoice=false
  // Invoice context: set includeInInvoice=true, includeInProposal=false
  const isProposalContext = !!filter.quoteDocumentId;
  const isInvoiceContext = !!filter.invoiceId;

  const eventPhoto = EventPhoto.create({
    eventId,
    fileId: fileRes.id,
    serviceItemId,
    ...(isProposalContext && { quoteDocumentId: filter.quoteDocumentId }),
    ...(isInvoiceContext && { invoiceId: filter.invoiceId }),
    includeInProposal: isProposalContext,
    includeInInvoice: isInvoiceContext,
  });

  const completePhoto = await EventClientService.CreateEventPhoto(eventPhoto);
  return completePhoto;
};
